import { Box } from '@mui/material';
import { formatTranslation } from 'intl';
import { Gallery } from 'components/common/gallery';
import { DocumentationViewItem } from 'store/documentation/model/types';
import {
  withDocumentationForm,
  useDocumentationForm,
} from 'components/dataCreationForms/withDocumentationForm';
import { withIssueForm } from 'components/common/withIssueForm';
import {
  listenToOpenDocumentationInput,
  OpenDocumentationInputEvent,
} from 'shared/domain/documentation/openDocumentationInput';
import { openDirectoryForm } from 'presentation/directory/openDirectoryInput';
import {
  ChangeEvent,
  ReactElement,
  Suspense,
  useCallback,
  useEffect,
  useState,
  useSyncExternalStore,
  lazy,
  useMemo,
  useRef,
} from 'react';
import { DocumentationEmpty } from './components/DocumentationEmpty';
import {
  DocumentationContextMenu,
  useContextMenu,
} from './components/DocumentationContextMenu';
import {
  useDocumentationGalleryStore,
  useDocumentationUrlNavigation,
} from 'store/documentation/hooks';
import { withStoreDependencies } from './components/StoreDependencies';
import { DataGridContainer } from 'components/common/dataGrid/container';
import { DataGrid } from 'components/common/dataGrid';
import { useDataGridApi } from 'components/common/withDataGridApi';
import { createClientDataGridConfig } from 'components/common/dataGrid/config/DataGridConfig';
import { getRowId, handleFilesUpload } from './model';
import { useDataGridState } from './hooks/useDataGridState';
import { useGridRowInteractions } from './hooks/useGridRowInteractions';
import { useGridRowSelection } from './hooks/useGridRowSelection';
import { useGridRowClassName } from './hooks/useGridRowClassName';
import { useGridSlotProps } from './hooks/useGridSlotProps';
import { DocumentationDataGridFloatingToolbar } from './components/DocumentationDataGridFloatingToolbar';

import { styles } from './styles';
import { useDragAndDrop } from './hooks/useDragAndDrop';
import { useGridLoadingState } from './hooks/useGridLoadingState';
import { useIssueOverlayControls } from './components/IssueOverlayControls';
import { useIssueGridColumnsConfig } from 'components/issue/IssueDataGrid/hooks/useIssueGridColumnsConfig';
import {
  useAppUrlParams,
  UrlParamName,
} from 'hooks/navigation/useAppUrlParams';
import {
  useIssuesGridFilterModel,
} from 'store/issue';
import { DataGridInstanceName } from 'services/storage/keys';
import { useIntl } from 'react-intl';
import { useDocumentationStore } from 'store/documentation';
import { isSearchActive } from 'store/documentation/slices/filters/model';
import { DocumentationMoveDialog } from './components/DocumentationMoveDialog';
import { BreadcrumbHoverBar } from './components/BreadcrumbHoverBar';
import { GridColumnFieldDocumentation } from 'components/common/dataGrid/columnsFactory/types';
import { composeHOCs } from 'shared/utils/composeHOCs';
import { DocumentationTranslationKeys } from 'intl/keys';

const DocumentationFilterPanel = lazy(() =>
  import('components/common/StandaloneFilterPanel').then((module) => ({
    default: module.StandaloneFilterPanel,
  }))
);

function _DocumentationDataGrid(): ReactElement {
  const intl = useIntl();

  const inputRef = useRef<HTMLInputElement>(null);
  const directoryInputRef = useRef<HTMLInputElement>(null);

  const { handleContextMenu, handleClose, contextMenu } = useContextMenu();
  const { filtersVisibleStore } = useIssueOverlayControls();
  const isFilterPanelOpen = useSyncExternalStore(
    filtersVisibleStore.subscribe,
    filtersVisibleStore.get
  );
  
  const {
    columns: issueColumns,
    slotProps: issueSlotProps,
  } = useIssueGridColumnsConfig();

  const {
    filterModel: issueFilterModel,
    onFilterModelChange: onIssueFilterModelChange,
  } = useIssuesGridFilterModel();

  const addDocumentationsFromFiles = useDocumentationStore.use.addDocumentationsFromFiles();
  const addDirectoriesFromFiles = useDocumentationStore.use.addDirectoriesFromFiles();
  const selectItems = useDocumentationStore.use.selectItems();
  const moveSelectedToDirectory = useDocumentationStore.use.moveSelectedToDirectory();
  const hoveredDirectoryId = useDocumentationStore.use.hoveredDirectoryId();

  const GalleryController = useDocumentationGalleryStore();

  const {
    setGallery,
    openEditComponent,
  } = GalleryController;
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);

  const apiRef = useDataGridApi();

  useEffect(() => {
    function openInput(
      event: CustomEvent<OpenDocumentationInputEvent>
    ): void {
      (event.detail?.webkitdirectory
        ? directoryInputRef
        : inputRef
      ).current?.click();
    }
    // @ts-expect-error CustomEvent listener signature is broader than the typed event
    return listenToOpenDocumentationInput(openInput);
  }, []);

  const {
    rows,
    columns,
    filterModel: dataGridFilterModel,
    setFilterModel,
    sortModel,
    setSortModel,
    searchPhrase,
    isLoadingDocumentations,
    isLoadingDirectories,
    isLoadingSearch,
    isFiltering,
    navigateToDirectory,
    clearFiltersAndNavigate,
  } = useDataGridState();

  const {
    isDocumentationListLoading,
    shouldShowGridLoadingOverlay,
    shouldShowNoRowsOverlayLoading,
    setFilteredFileResetDialogOpen,
  } = useGridLoadingState({
    isLoadingDocumentations,
    isLoadingDirectories,
    isLoadingSearch,
    isLoadingFilters: isFiltering,
    searchPhrase,
  });

  const fileId = useAppUrlParams(UrlParamName.FILE_ID);
  const galleryOpen = useDocumentationStore.use.galleryOpen();
  const urlFileOpenPending = useDocumentationStore.use.urlFileOpenPending();
  const shouldMaskGridForUrlFile =
    Boolean(fileId) && !galleryOpen && urlFileOpenPending;
  const gridLoadingOverlay =
    shouldShowGridLoadingOverlay || shouldMaskGridForUrlFile;


  const handleAddDocumentationsFromFiles = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      handleFilesUpload(e.target.files, addDocumentationsFromFiles, e);
    },
    [addDocumentationsFromFiles]
  );

  const handleAddDirectoriesFromFiles = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      handleFilesUpload(e.target.files, addDirectoriesFromFiles, e);
    },
    [addDirectoriesFromFiles]
  );

  const openMoveDialog = useCallback(() => {
    setIsMoveDialogOpen(true);
  }, []);

  const closeMoveDialog = useCallback(() => {
    setIsMoveDialogOpen(false);
  }, []);

  const handleFilesDropRef = useRef<
    (
      files: File[],
      hasDirectories: boolean,
      targetDirectoryId: string | null,
      emptyDirectoryPaths: string[]
    ) => void
  >();

  handleFilesDropRef.current = (
    files,
    hasDirectories,
    targetDirectoryId,
    emptyDirectoryPaths
  ) => {
    if (hasDirectories) {
      addDirectoriesFromFiles(files, targetDirectoryId, emptyDirectoryPaths);
    } else {
      addDocumentationsFromFiles(files, targetDirectoryId);
    }
  };

  const dragAndDrop = useDragAndDrop({
    rows: rows,
    getRowId,
    setParent: moveSelectedToDirectory,
    selectElements: selectItems,
    onFilesDrop: (files, hasDirs, targetId, emptyDirectoryPaths) => {
      handleFilesDropRef.current?.(files, hasDirs, targetId, emptyDirectoryPaths);
    },
  });

  const { handleRowDoubleClick, handleRowContextMenu } = useGridRowInteractions({
    rows,
    setGallery,
    navigateToDirectory,
    openContextMenu: handleContextMenu,
  });
  const { handleSelectionChange } = useGridRowSelection(rows, apiRef);
  const getRowClassName = useGridRowClassName({
    isDraggingOver: dragAndDrop.isDraggingOver,
    dropTargetRef: dragAndDrop.dropTargetRef,
  });

  const { isOpen: isDocumentationDialogOpen } = useDocumentationForm();

  useDocumentationUrlNavigation(
    clearFiltersAndNavigate,
    isDocumentationListLoading,
    setFilteredFileResetDialogOpen,
    isDocumentationDialogOpen
  );

  // Toggle parentDirectory column visibility based on search mode
  useEffect(() => {
    apiRef.current?.setColumnVisibility?.(
      GridColumnFieldDocumentation.parentDirectory,
      isSearchActive({ searchPhrase })
    );
  }, [searchPhrase, apiRef]);

  const baseConfig = useMemo(
    () =>
      createClientDataGridConfig<DocumentationViewItem>(
        DataGridInstanceName.documentation,
        []
      )
        .withServerSideOperations()
        .withGetRowId(getRowId)
        .withRowSelection({
          disableMultipleRowSelection: false,
          hideFooterSelectedRowCount: true,
        })
        .withDisablePagination()
        .withInitialColumnVisibilityModel({
          [GridColumnFieldDocumentation.parentDirectory]: false,
        }),
    []
  );

  const tooltipText = formatTranslation(intl, DocumentationTranslationKeys.directoryContainsMatchingFiles,);

  const slotProps = useGridSlotProps({
    onRowContextMenu: handleRowContextMenu,
    ...dragAndDrop.rowProps,
    columns,
    tooltipText,
  });

  const handleFilterPanelClose = useCallback(() => {
    filtersVisibleStore.set(false);
  }, [filtersVisibleStore]);

  const config = useMemo(
    () =>
      baseConfig
        .withRowDoubleClick(handleRowDoubleClick)
        .withControlledSorting(sortModel, setSortModel)
        .withControlledFiltering(dataGridFilterModel, setFilterModel)
        .withRowClassName(getRowClassName)
        .withControlledRowSelection(undefined, handleSelectionChange)
        .withNoRowsOverlay(() => (
          <DocumentationEmpty
            searchPhrase={searchPhrase}
            loading={shouldShowNoRowsOverlayLoading}
            hasData={rows.length > 0}
          />
        ))
        .withSlotProps(slotProps)
        .withAutosizing()
        .build(),
    [
      baseConfig,
      handleRowDoubleClick,
      sortModel,
      dataGridFilterModel,
      getRowClassName,
      handleSelectionChange,
      slotProps,
      searchPhrase,
      shouldShowNoRowsOverlayLoading,
      rows.length,
    ]
  );

  return (
    <>
      <>
        <Box data-breadcrumb-container sx={{ position: 'relative', height: '100%', width: '100%' }}>
          <BreadcrumbHoverBar visible={Boolean(hoveredDirectoryId)} />
          <DataGridContainer {...dragAndDrop.containerProps}>
            <DataGrid
              data={rows}
              columns={columns}
              config={config}
              loading={gridLoadingOverlay}
              styles={styles}
            />
          </DataGridContainer>
        </Box>
        {dragAndDrop.snackbar}
        <Gallery {...GalleryController} />
        <Suspense fallback={null}>
          <DocumentationFilterPanel
            open={isFilterPanelOpen}
            onClose={handleFilterPanelClose}
            columns={issueColumns}
            filterModel={issueFilterModel}
            onFilterModelChange={onIssueFilterModelChange}
            slotProps={issueSlotProps}
          />
        </Suspense>
        <DocumentationContextMenu
          contextMenu={contextMenu}
          handleClose={handleClose}
          editFile={openEditComponent}
          editDirectory={openDirectoryForm}
          filterModel={dataGridFilterModel}
          onMove={openMoveDialog}
        />
        <DocumentationDataGridFloatingToolbar
          onMoveSelection={openMoveDialog}
        />
        <DocumentationMoveDialog
          isOpen={isMoveDialogOpen}
          onClose={closeMoveDialog}
        />
      </>

      <div style={{ width: 0, height: 0 }}>
        <input
          ref={inputRef}
          style={{ width: 0, height: 0 }}
          type='file'
          onChange={handleAddDocumentationsFromFiles}
          multiple
        />
        <input
          ref={directoryInputRef}
          style={{ width: 0, height: 0 }}
          type='file'
          onChange={handleAddDirectoriesFromFiles}
          multiple
          // @ts-expect-error webkitdirectory is a non-standard attribute missing from React types
          webkitdirectory='true'
        />
      </div>
    </>
  );
}

export const DocumentationDataGrid = composeHOCs(
  withIssueForm,
  withDocumentationForm,
  withStoreDependencies
)(_DocumentationDataGrid);
